import { formatQuantity } from '../../utils/inventoryLabels';

/*
 * Storing and moving are one act with two faces: **moving** takes stock that is
 * already put away to another position, and **storing** takes it off the
 * receiving dock and puts it in its place. For whoever operates it is the same
 * gesture; the difference is the zone it leaves from.
 */

export const DEFAULT_TRANSFER_NOTES = 'Traslado desde inventario';

/**
 * Identity of a place stock can leave from: a location and, inside it, a lot.
 * The same location can hold two lots of one product, and they are two origins.
 */
export const bucketKey = (bucket) => `${bucket.slot}|${bucket.lot || ''}`;

/**
 * The rows stock can be moved out of.
 *
 * Only what is available and sitting in a location: reserved stock is spoken
 * for, and what is on the packing table does not live in any position.
 *
 * @param {Array<object>} rows - The detail by location of one product.
 */
export const transferableBuckets = (rows) =>
  rows.filter((row) => row.state === 'available' && row.slot);

/**
 * What is still missing before the transfer can be sent, as the sentence the
 * button shows while it is disabled — or `null` when it is ready to go.
 *
 * @param {object} form
 * @param {number|string|null} form.productId
 * @param {object|null} form.from - The origin bucket.
 * @param {number|string|null} form.toSlotId
 * @param {string} form.quantity
 * @returns {string|null}
 */
export const transferProblem = ({ productId, from, toSlotId, quantity }) => {
  if (!productId) return 'Elige el producto';
  if (!from) return 'Elige de dónde sale';
  if (!toSlotId) return 'Elige a dónde va';

  const amount = Number(quantity);
  if (!quantity || Number.isNaN(amount) || amount <= 0) return 'Indica cuánto mover';

  const available = Number(from.quantity);
  if (amount > available) return `En ${from.slot_name} solo hay ${formatQuantity(available)}`;

  return null;
};

/**
 * The body of the transfer. The lot is the origin's: a location can hold two
 * lots of one product, and the server needs to know which one is leaving.
 */
export const buildTransferPayload = ({ productId, from, toSlotId, quantity, notes }) => ({
  product_id: productId,
  from_slot_id: from.slot,
  to_slot_id: toSlotId,
  quantity,
  lot: from.lot || '',
  notes: (notes || '').trim() || DEFAULT_TRANSFER_NOTES,
});
